import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { OverlayPage } from '@/components/overlay-page';
import { EmptyState } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { pdfAction } from '@/lib/pdf';
import { errorText } from '@/lib/portal';
import { ltrIn } from '@/lib/sales';
import {
  STATEMENT_TYPES,
  defaultStart,
  statementBody,
  type StatementCustomer,
  type StatementSendResult,
  type StatementType,
} from '@/lib/statements';
import { homeCurrency } from '@/lib/org-locale';

export const Route = createFileRoute('/sales/statements/new')({
  component: CreateStatements,
  validateSearch: (s: Record<string, unknown>): { customerId?: string } =>
    typeof s['customerId'] === 'string' ? { customerId: s['customerId'] } : {},
});

type Show = 'balance' | 'overdue' | 'all';

/**
 * Create statements (QBO + New › Statement; document 06 "Statements"): type, statement date and
 * period, the customers to include; preview or print them as one PDF, or email each customer.
 */
function CreateStatements() {
  const t = useT();
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const { customerId } = Route.useSearch();
  const [type, setType] = useState<StatementType>('balance_forward');
  const [statementDate, setStatementDate] = useState(todayIso());
  const [from, setFrom] = useState(defaultStart(todayIso()));
  const [show, setShow] = useState<Show>(customerId ? 'all' : 'balance');
  const [picked, setPicked] = useState<Set<string> | null>(
    customerId ? new Set([customerId]) : null,
  );
  const [errors, setErrors] = useState<string[]>([]);
  const [result, setResult] = useState<StatementSendResult | null>(null);
  const customers = useQuery({
    queryKey: ['statements', orgId, 'customers', statementDate],
    queryFn: () =>
      api<StatementCustomer[]>(
        `/orgs/${orgId}/statements/customers?statementDate=${statementDate}`,
      ),
    enabled: Boolean(orgId && statementDate),
  });
  const shown = useMemo(
    () =>
      (customers.data ?? []).filter((c) =>
        show === 'all' ? true : show === 'overdue' ? c.overdue !== '0.00' : c.balance !== '0.00',
      ),
    [customers.data, show],
  );
  // by default every customer shown is ticked
  const selected = picked ?? new Set(shown.map((c) => c.id));
  const ids = shown.filter((c) => selected.has(c.id)).map((c) => c.id);
  const total = shown
    .filter((c) => selected.has(c.id))
    .reduce((s, c) => s + (toMinor(c.balance) ?? 0n), 0n);

  const check = (): boolean => {
    const p: string[] = [];
    if (!statementDate) p.push(t('salesPages.statementNew.enterDate'));
    if (!ids.length) p.push(t('salesPages.statementNew.tickCustomer'));
    if (type !== 'open_item' && (!from || from > statementDate))
      p.push(t('salesPages.statementNew.startAfterDate'));
    setErrors(p);
    return !p.length;
  };
  const body = () => statementBody({ type, statementDate, from, customerIds: ids });
  const send = useMutation({
    mutationFn: () =>
      api<StatementSendResult>(`/orgs/${orgId}/statements/send`, {
        method: 'POST',
        body: JSON.stringify(body()),
      }),
    onSuccess: (r) => {
      setResult(r);
      toast(
        r.skipped.length
          ? t('salesPages.statementNew.sentSkipped', {
              count: r.sent.length,
              skipped: r.skipped.length,
            })
          : t('salesPages.statementNew.sent', { count: r.sent.length }),
        r.skipped.length ? { tone: 'error' } : {},
      );
    },
    onError: (e) => setErrors([errorText(e)]),
  });
  const close = () => void navigate({ to: '/sales/customers' });
  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  const isStaff = me.data?.isStaff ?? false;
  return (
    <OverlayPage
      title={t('salesPages.statementNew.title')}
      onClose={close}
      footerLeft={
        <Button variant="outline" onClick={close}>
          {t('common.cancel')}
        </Button>
      }
      footerCentre={
        <Button
          variant="ghost"
          onClick={() => {
            if (check())
              void pdfAction('preview', `/orgs/${orgId}/statements/pdf`, 'Statements.pdf', {
                method: 'POST',
                body: JSON.stringify(body()),
              });
          }}
        >
          {t('salesPages.statementNew.printPreview')}
        </Button>
      }
      footerRight={
        isStaff ? (
          <Button disabled={send.isPending} onClick={() => check() && send.mutate()}>
            {send.isPending
              ? t('salesPages.statementNew.sending')
              : t('salesPages.statementNew.saveAndSend')}
          </Button>
        ) : null
      }
    >
      <div className="mx-auto flex max-w-[1100px] flex-col gap-5">
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc ps-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        {result ? (
          <div
            className="rounded-md border bg-muted/40 px-3 py-2 text-sm"
            data-testid="statement-result"
          >
            {result.sent.length ? (
              <p>
                {t(
                  result.sent.some((s) => s.status === 'recorded')
                    ? 'salesPages.statementNew.sentToRecorded'
                    : 'salesPages.statementNew.sentTo',
                  {
                    list: result.sent
                      .map((s) =>
                        t('salesPages.statementNew.recipient', {
                          name: s.customerName,
                          email: ltrIn(s.to),
                        }),
                      )
                      .join(t('salesPages.statementNew.listSeparator')),
                  },
                )}
              </p>
            ) : null}
            {result.skipped.map((s) => (
              <p key={s.customerId} className="text-destructive">
                {s.customerName}: {s.reason}
              </p>
            ))}
          </div>
        ) : null}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
          <Field label={t('salesPages.statementNew.statementType')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={type}
                onChange={(e) => setType(e.target.value as StatementType)}
              >
                {STATEMENT_TYPES.map(([k]) => (
                  <option key={k} value={k}>
                    {t(`salesPages.statementNew.types.${k}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('salesPages.statementNew.statementDate')}>
            {(id) => <DateInput id={id} value={statementDate} onChange={setStatementDate} />}
          </Field>
          {type !== 'open_item' ? (
            <>
              <Field label={t('salesPages.statementNew.startDate')}>
                {(id) => <DateInput id={id} value={from} onChange={setFrom} />}
              </Field>
              <Field label={t('salesPages.statementNew.endDate')}>
                {(id) => <DateInput id={id} value={statementDate} onChange={setStatementDate} />}
              </Field>
            </>
          ) : null}
          <Field label={t('salesPages.statementNew.customers')}>
            {(id) => (
              <NativeSelect
                id={id}
                value={show}
                onChange={(e) => {
                  setShow(e.target.value as Show);
                  setPicked(null);
                }}
              >
                <option value="balance">{t('salesPages.statementNew.withBalance')}</option>
                <option value="overdue">{t('salesPages.statementNew.withOverdue')}</option>
                <option value="all">{t('salesPages.statementNew.allCustomers')}</option>
              </NativeSelect>
            )}
          </Field>
        </div>
        <p className="text-sm text-muted-foreground">
          {type === 'balance_forward'
            ? t('salesPages.statementNew.explainBalanceForward', {
                from: formatDate(from),
                to: formatDate(statementDate),
              })
            : type === 'open_item'
              ? t('salesPages.statementNew.explainOpenItem', { to: formatDate(statementDate) })
              : t('salesPages.statementNew.explainTransaction', {
                  from: formatDate(from),
                  to: formatDate(statementDate),
                })}
        </p>
        {customers.isPending ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : !shown.length ? (
          <EmptyState
            title={t('salesPages.statementNew.noCustomers')}
            hint={t('salesPages.statementNew.noCustomersHint')}
          />
        ) : (
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary"
                      aria-label={t('salesPages.statementNew.selectAll')}
                      checked={ids.length === shown.length}
                      onChange={(e) =>
                        setPicked(e.target.checked ? new Set(shown.map((c) => c.id)) : new Set())
                      }
                    />
                  </TableHead>
                  <TableHead>{t('salesPages.statementNew.colCustomer')}</TableHead>
                  <TableHead>{t('salesPages.statementNew.colEmail')}</TableHead>
                  <TableHead className="w-[150px] text-end">
                    {t('salesPages.statementNew.colOverdue')}
                  </TableHead>
                  <TableHead className="w-[150px] text-end">
                    {t('salesPages.statementNew.colBalance')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((c) => (
                  <TableRow key={c.id} data-testid="statement-customer">
                    <TableCell>
                      <input
                        type="checkbox"
                        className="size-4 accent-primary"
                        aria-label={t('salesPages.statementNew.include', { name: c.name })}
                        checked={selected.has(c.id)}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) next.add(c.id);
                          else next.delete(c.id);
                          setPicked(next);
                        }}
                      />
                    </TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell className={c.email ? '' : 'text-muted-foreground'}>
                      {c.email ?? t('salesPages.statementNew.noEmail')}
                    </TableCell>
                    <TableCell className="tnum text-end">{formatAmount(c.overdue)}</TableCell>
                    <TableCell className="tnum text-end">{formatAmount(c.balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex justify-between border-t px-4 py-2 text-sm">
              <span>{t('salesPages.statementNew.selected', { count: ids.length })}</span>
              <span className="tnum font-medium">
                {t('salesPages.statementNew.total', {
                  amount: ltrIn(`${homeCurrency()} ${formatMinor(total)}`),
                })}
              </span>
            </div>
          </div>
        )}
      </div>
    </OverlayPage>
  );
}
